import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, DatePicker, TextBox } from "../../../../components";
import { useNavigate } from "react-router-dom";
import CreateTenderingContext, {
  TENDERING_CREATE_STEPS,
} from "../CreateTenderingContext";
import { Field, Form, Formik } from "formik";
import TextArea from "../../../../components/common/TextArea";
import ComboBox from "../../../../components/common/ComboBox";
import AppContext from "../../../../AppContext";
import _ from "lodash";
import TextBoxWithSelect from "../../../../components/common/TextBoxWithSelect";
import { addDays, startOfDay } from "date-fns";
import { provinces } from "../../../../helpers/common.helper";
import { ChoiceChip } from "../../../../components/common/ChoiceChip";

const Split = ({ gridTemplateColumns, columns, children, style }) => {
  const _style = {
    gridTemplateColumns: `repeat(${columns ?? 2}, minmax(auto, 1fr))`,
  };

  if (gridTemplateColumns) {
    _style.gridTemplateColumns = gridTemplateColumns;
  }

  return (
    <div
      className="tendering-create__design-info__form__split"
      style={{ ..._style, ...style }}
    >
      {children}
    </div>
  );
};

const ConstructionInfomation = (props) => {
  const navigate = useNavigate();
  const { designStyles } = useContext(AppContext);
  const { goBackward, goForward, details, setDetails, setCurrentStep } =
    useContext(CreateTenderingContext);
  const [info, setInfo] = useState(
    details?.info ?? {
      name: "Test",
      desc: "Description",
      startDate: startOfDay(new Date()),
      endDate: addDays(startOfDay(new Date()), 1),
      typeOfWork: "64b3fe4e5be22ea1f6856462",
      typeOfSpace: "64b3fe3e5be22ea1f685643f",
      typeOfStyles: ["64b3fe475be22ea1f6856455"],
      location: "Bangkok",
      googleMap: "https://maps.app.goo.gl/N6yLcf8fdcYNnq3V9",
      duration: 1,
      durationUnit: "days",
      budget: 100000,
      bankGuarantee: "10",
      taxRequirement: "vat",
      areaSize: 200,
      areaUnit: "sq.m.",
      retention: true,
    }
  );
  const [selectedType, setSelectedType] = useState(undefined);

  useEffect(() => {
    setCurrentStep(TENDERING_CREATE_STEPS.INFO);
  }, []);

  const handleGoForward = () => {
    setDetails((curr) => ({ ...curr, type: selectedType }));
    goForward();
  };

  const handleValidate = ({
    name,
    desc,
    startDate,
    endDate,
    typeOfWork,
    typeOfSpace,
    typeOfStyles,
    location,
    googleMap,
    duration,
    durationUnit,
    bankGuarantee,
    taxRequirement,
  }) => {
    let errors = {};

    if (!name) errors.name = "Name is required";
    if (!desc) errors.desc = "A description is required";
    if (!startDate) errors.startDate = "Date is required";
    if (!endDate) errors.endDate = "Date is required";
    if (!typeOfWork) errors.typeOfWork = "Is required";
    if (!typeOfSpace) errors.typeOfSpace = "Is required";
    if (typeOfStyles.length === 0) errors.typeOfStyles = "Is required";
    if (!location) errors.location = "Is required";
    if (!googleMap) errors.googleMap = "Is required";
    if (!duration) errors.duration = "Is required";
    if (!durationUnit) errors.durationUnit = "Is required";
    if (!bankGuarantee) errors.bankGuarantee = "Is required";
    if (!taxRequirement) errors.taxRequirement = "Is required";

    if (startDate && endDate) {
      if (startDate > endDate) {
        errors.startDate = "Cannot be after end date";
        errors.endDate = "Cannot be before end date";
      }
    }

    return errors;
  };

  const handleSubmit = (values, options) => {
    setDetails((curr) => ({
      ...curr,
      info: values,
    }));

    goForward();
  };

  return (
    <div className="tendering-create__design-info">
      <h3>Create Tendering</h3>
      <Formik
        initialValues={info}
        validate={handleValidate}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <Form className="tendering-create__design-info__form">
            <h4>Basic Information</h4>
            <TextBox name="name" label="Name" />
            <TextArea name="desc" label="Description" />
            <Split>
              <DatePicker
                name="startDate"
                label={"Tendering Period"}
                dateFormat="dd/MM/yyyy"
                placeholder={"Start Date"}
              />
              <DatePicker
                name="endDate"
                dateFormat="dd/MM/yyyy"
                placeholder={"End Date"}
              />
            </Split>
            <h4>Scopes of Work</h4>
            <Split>
              <ComboBox
                name="typeOfWork"
                label="Type of work"
                options={designStyles.works}
              />
              <ComboBox
                name="typeOfSpace"
                label="Type of space"
                options={designStyles.spaces}
              />
            </Split>
            <ComboBox
              name="typeOfStyles"
              label="Type of styles"
              multi={true}
              options={designStyles.styles}
            />
            <Split>
              <ComboBox name="location" label="Location" options={provinces} />
              <TextBox name="googleMap" label="Google Map Link" />
            </Split>
            <Split columns={4}>
              <TextBox
                name="areaSize"
                label="Area size"
                type="number"
                withOption={true}
                options={[
                  { value: "days", name: "Days" },
                  { value: "months", name: "Months" },
                  { value: "years", name: "Years" },
                ]}
                width={130}
              />
              <ComboBox
                name="areaUnit"
                multi={false}
                options={[{ value: "sq.m.", name: "sq.m." }]}
                width={106}
              />
              <TextBox
                name="duration"
                label="Project duration"
                type="number"
                withOption={true}
                options={[
                  { value: "days", name: "Days" },
                  { value: "months", name: "Months" },
                  { value: "years", name: "Years" },
                ]}
                width={110}
              />
              <ComboBox
                name="durationUnit"
                multi={false}
                options={[
                  { value: "days", name: "Days" },
                  { value: "months", name: "Months" },
                  { value: "years", name: "Years" },
                ]}
                width={126}
              />
            </Split>
            <h4>Project Budget</h4>
            <TextBox
              name="budget"
              label="Estimated Budget (Optional)"
              type="number"
            />
            <Split columns={3}>
              <ComboBox
                name="bankGuarantee"
                label="Bank Guarantee"
                multi={false}
                options={[
                  { value: "10", name: "10%" },
                  { value: "7", name: "7%" },
                  { value: "5", name: "5%" },
                ]}
              />
              <div className="tendering-create__design-info__form__tax">
                <span className="tendering-create__design-info__form__tax__title">
                  Tax Requirement
                </span>
                <div className="tendering-create__design-info__form__tax__choices">
                  <ChoiceChip
                    label={"VAT 7%"}
                    onClick={() => {
                      props.setFieldValue("taxRequirement", "vat");
                    }}
                    isSelected={props.values.taxRequirement === "vat"}
                    height={28}
                  />
                  <ChoiceChip
                    label={"Tax 3%"}
                    onClick={() => {
                      props.setFieldValue("taxRequirement", "tax");
                    }}
                    isSelected={props.values.taxRequirement === "tax"}
                    height={28}
                  />
                </div>
              </div>
              <div className="tendering-create__design-info__form__retention">
                <label className="tendering-create__design-info__form__retention__checkbox">
                  <Field type="checkbox" name="retention" />
                  Retention
                </label>
                <p className="tendering-create__design-info__form__retention__desc">
                  Check the box if you wish to provide a 6-month warranty for
                  the project by withholding 5% of the total budget. The system
                  will release the withheld amount after the completion of the
                  6-month period."
                </p>
              </div>
            </Split>
            <div className="tendering-create__design-info__footer">
              <Button
                label="Back"
                variant={"secondary"}
                onClick={() => goBackward()}
              />
              <Button label="Next" type="submit" />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

ConstructionInfomation.propTypes = {};

export default ConstructionInfomation;
